import { compareOptional } from '../../shared/results_page/results_sort.js';
import type { TimeRange } from '../../shared/results_page/time_range_section.js';
import type { BusOfferView } from '../models.js';

/** Os FATOS de corte de uma viagem, ao lado da view que a lista mostra — o
 *  mesmo desenho do `RentalFilterableOffer`: a view é prosa, os fatos são
 *  números e ids. */
export type BusFilterableOffer = {
  offer: BusOfferView;
  withinPolicy: boolean;
  /** O preço POR PESSOA, em unidades da moeda da lista. */
  price?: number;
  /** A classe como o bus-api a descreve ("Semi leito") — o id do corte. */
  seatClassId: string;
  companyId: string;
  /** Minutos desde a meia-noite, no horário LOCAL da rodoviária. */
  departureMinute: number;
  arrivalMinute: number;
  durationMinutes: number;
  /** Quantas preferências do viajante a viagem atende (viação, classe) — as
   *  que atendem mais vêm antes, dentro da ordem escolhida. Ausente = 0. */
  preferenceRank?: number;
};

export type BusResultsFilterValue = {
  readonly policyOnly: boolean;
  /** Classes em `or`. Vazio = eixo neutro. */
  readonly seatClassIds: readonly string[];
  /** `undefined` = o dia inteiro (eixo neutro). */
  readonly departure?: TimeRange;
  readonly arrival?: TimeRange;
  /** Piso/teto do preço por pessoa ("Preço até"). `undefined` = ponta. */
  readonly minimumPrice?: number;
  readonly maximumPrice?: number;
  /** Viações em `or`. Vazio = eixo neutro. */
  readonly companyIds: readonly string[];
};

export const EMPTY_BUS_RESULTS_FILTERS: BusResultsFilterValue = {
  policyOnly: false,
  seatClassIds: [],
  companyIds: [],
};

export type BusFilterAxis =
  | 'policyOnly'
  | 'seatClassIds'
  | 'departure'
  | 'arrival'
  | 'price'
  | 'companyIds';

function withinMinutes(minute: number, range: TimeRange | undefined): boolean {
  return range === undefined || (minute >= range.from && minute <= range.to);
}

/** Sem preço, a viagem SAI quando há corte de preço ativo — um número
 *  desconhecido não prova que cabe na régua (a regra do carro). */
function withinPrice(facts: BusFilterableOffer, filters: BusResultsFilterValue): boolean {
  if (filters.minimumPrice === undefined && filters.maximumPrice === undefined) return true;
  if (facts.price === undefined) return false;
  return (
    (filters.minimumPrice === undefined || facts.price >= filters.minimumPrice) &&
    (filters.maximumPrice === undefined || facts.price <= filters.maximumPrice)
  );
}

/** O corte completo, puro — cada eixo é um `and`; `ignoredAxis` tira UM eixo
 *  da conta, o que faz a contagem de cada opção ser honesta. */
export function matchesBusResultsFilters(
  facts: BusFilterableOffer,
  filters: BusResultsFilterValue,
  ignoredAxis?: BusFilterAxis,
): boolean {
  const skip = (axis: BusFilterAxis) => ignoredAxis === axis;
  return (
    (skip('policyOnly') || !filters.policyOnly || facts.withinPolicy) &&
    (skip('seatClassIds') ||
      filters.seatClassIds.length === 0 ||
      filters.seatClassIds.includes(facts.seatClassId)) &&
    (skip('departure') || withinMinutes(facts.departureMinute, filters.departure)) &&
    (skip('arrival') || withinMinutes(facts.arrivalMinute, filters.arrival)) &&
    (skip('price') || withinPrice(facts, filters)) &&
    (skip('companyIds') ||
      filters.companyIds.length === 0 ||
      filters.companyIds.includes(facts.companyId))
  );
}

/** Quantos eixos cortam — o contador do botão `Filtros`. */
export function activeBusFilterCount(filters: BusResultsFilterValue): number {
  return [
    filters.policyOnly,
    filters.seatClassIds.length > 0,
    filters.departure !== undefined,
    filters.arrival !== undefined,
    filters.minimumPrice !== undefined || filters.maximumPrice !== undefined,
    filters.companyIds.length > 0,
  ].filter(Boolean).length;
}

export type BusSeatClassOption = { id: string; label: string; fromPrice: number | undefined };
export type BusCompanyOption = { id: string; label: string };

/**
 * As réguas que saem do CATÁLOGO em cena: a faixa de preço, as classes com o
 * MENOR preço de cada uma (decisão da #2440: o valor ao lado da classe é o
 * mínimo dela) e as viações em ordem alfabética.
 *
 * O valor da classe conta só as viagens que passam nos DEMAIS cortes (a régua da
 * contagem de viação): com horário ou preço cortando, um mínimo da lista inteira
 * anunciava um preço que nenhum card mostra (#6420). Já a lista e a ORDEM das
 * classes, e a faixa de preço, saem da lista inteira — nenhuma classe some nem
 * troca de lugar enquanto se corta, e a régua de preço segue podendo ALARGAR.
 */
export function busFilterCatalog(
  offers: readonly BusFilterableOffer[],
  filters: BusResultsFilterValue = EMPTY_BUS_RESULTS_FILTERS,
) {
  const prices = offers.flatMap(({ price }) => (price === undefined ? [] : [price]));
  const seatClasses = new Map<string, BusSeatClassOption>();
  const floors = new Map<string, number | undefined>();
  const companies = new Map<string, BusCompanyOption>();
  const lowest = (current: number | undefined, price: number | undefined) =>
    price === undefined || (current !== undefined && current <= price) ? current : price;
  for (const facts of offers) {
    const { offer, seatClassId, companyId, price } = facts;
    const seatClass = seatClasses.get(seatClassId) ?? {
      id: seatClassId,
      label: offer.seatClassLabel,
      fromPrice: undefined,
    };
    floors.set(seatClassId, lowest(floors.get(seatClassId), price));
    if (matchesBusResultsFilters(facts, filters, 'seatClassIds')) {
      seatClass.fromPrice = lowest(seatClass.fromPrice, price);
    }
    seatClasses.set(seatClassId, seatClass);
    if (!companies.has(companyId)) {
      companies.set(companyId, { id: companyId, label: offer.company.name });
    }
  }
  return {
    priceFloor: prices.length === 0 ? undefined : Math.min(...prices),
    priceCeiling: prices.length === 0 ? undefined : Math.max(...prices),
    seatClasses: [...seatClasses.values()].sort((a, b) =>
      compareOptional(floors.get(a.id), floors.get(b.id), 'asc'),
    ),
    companies: [...companies.values()].sort((a, b) => a.label.localeCompare(b.label)),
  };
}

/** Os critérios do ônibus. O padrão é o horário de partida (#2440). */
export type BusResultsOrder = 'departure' | 'arrival' | 'price' | 'duration';

export const BUS_RESULTS_ORDERS: readonly BusResultsOrder[] = [
  'departure',
  'arrival',
  'price',
  'duration',
];

export const DEFAULT_BUS_RESULTS_ORDER: BusResultsOrder = 'departure';

/** A ordenação, pura e estável (empate preserva a ordem de entrada). */
export function sortBusOffers(
  offers: readonly BusFilterableOffer[],
  order: BusResultsOrder,
): BusFilterableOffer[] {
  // As preferidas na frente, sem mexer na ordem dentro de cada faixa: o `sort`
  // é estável, então a ordem escolhida segue valendo entre as preferidas e entre
  // as demais. Nunca filtra — a viagem que não atende continua na lista.
  return byOrder(offers, order).sort((a, b) => (b.preferenceRank ?? 0) - (a.preferenceRank ?? 0));
}

function byOrder(
  offers: readonly BusFilterableOffer[],
  order: BusResultsOrder,
): BusFilterableOffer[] {
  const sorted = [...offers];
  switch (order) {
    case 'departure':
      return sorted.sort((a, b) => a.departureMinute - b.departureMinute);
    case 'arrival':
      // Chegada ABSOLUTA (partida do dia + duração): `arrivalMinute` é só a hora
      // do dia, e uma chegada às 05:00 de amanhã ordenaria antes das 23:00 de hoje.
      return sorted.sort(
        (a, b) => a.departureMinute + a.durationMinutes - (b.departureMinute + b.durationMinutes),
      );
    case 'price':
      return sorted.sort((a, b) => compareOptional(a.price, b.price, 'asc'));
    case 'duration':
      return sorted.sort((a, b) => a.durationMinutes - b.durationMinutes);
    default: {
      const exhaustive: never = order;
      return exhaustive;
    }
  }
}

/** "08:30" de 510 minutos — a régua do rótulo das faixas de horário. */
export function minuteLabel(minute: number): string {
  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}
